import React, { useState, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import { Chart, registerables } from "chart.js";
Chart.register(...registerables);

const fmt = (n) => "₪" + Math.round(n).toLocaleString();

const inp =
  "w-full px-3 py-2 rounded-xl text-sm bg-gray-50 border border-gray-200 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 transition-all placeholder-gray-300";

function getMonthsBetween(start, end) {
  if (!start || !end) return [];
  const [sy, sm] = start.split("-").map(Number);
  const [ey, em] = end.split("-").map(Number);
  const result = [];
  let y = sy,
    m = sm;
  while (y < ey || (y === ey && m <= em)) {
    result.push({
      key: `${y}-${String(m).padStart(2, "0")}`,
      label: new Date(y, m - 1).toLocaleDateString("en", {
        month: "short",
        year: "numeric",
      }),
    });
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  return result;
}

const newMonthData = (key, label) => ({
  key,
  label,
  open: false,
  billedAmount: "",
  workers: [{ name: "", daily: "", days: "", type: "exp" }],
  equipment: [{ name: "", cost: "", type: "daily", days: "" }],
  transport: { type: "fixed", daily: "", days: "", total: "" },
  expenses: [{ label: "Materials", amount: "" }],
});

function monthCost(m, fixedCosts) {
  const fc = fixedCosts.reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);
  const wc = m.workers.reduce(
    (s, w) => s + (parseFloat(w.daily) || 0) * (parseFloat(w.days) || 0),
    0,
  );
  const ec = m.equipment.reduce((s, e) => {
    if (e.type === "fixed") return s + (parseFloat(e.cost) || 0);
    return s + (parseFloat(e.cost) || 0) * (parseFloat(e.days) || 0);
  }, 0);
  const tr =
    m.transport && typeof m.transport === "object"
      ? m.transport.type === "daily"
        ? (parseFloat(m.transport.daily) || 0) *
          (parseFloat(m.transport.days) || 0)
        : parseFloat(m.transport.total) || 0
      : parseFloat(m.transport) || 0;
  const ot = (m.expenses || []).reduce(
    (s, e) => s + (parseFloat(e.amount) || 0),
    0,
  );
  return { fc, wc, ec, tr, ot, total: fc + wc + ec + tr + ot };
}

function Field({ label, suffix, children }) {
  return (
    <div>
      <p className="text-xs font-medium text-slate-400 mb-1.5 tracking-wide uppercase">
        {label}
      </p>
      <div className="flex items-center gap-2">
        {children}
        {suffix && (
          <span className="text-xs text-slate-400 font-medium flex-shrink-0">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

function SectionLabel({ icon, title }) {
  return (
    <div className="flex items-center gap-2.5 mt-8 mb-3">
      <div className="w-7 h-7 rounded-lg bg-slate-800 flex items-center justify-center text-white text-sm flex-shrink-0">
        {icon}
      </div>
      <span className="text-sm font-semibold text-slate-700 tracking-wide">
        {title}
      </span>
    </div>
  );
}

function CashFlowChart({ months, fixedCosts, contract }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    if (chartRef.current) chartRef.current.destroy();
    const labels = [],
      incomes = [],
      costs = [],
      cumProfits = [];
    let cumProfit = 0;
    months.forEach((m) => {
      const income = parseFloat(m.billedAmount) || 0;
      const c = monthCost(m, fixedCosts);
      cumProfit += income - c.total;
      labels.push(m.label);
      incomes.push(Math.round(income));
      costs.push(Math.round(c.total));
      cumProfits.push(Math.round(cumProfit));
    });
    chartRef.current = new Chart(canvasRef.current, {
      data: {
        labels,
        datasets: [
          {
            type: "bar",
            label: "Income",
            data: incomes,
            backgroundColor: "rgba(29,158,117,0.7)",
            borderColor: "#1D9E75",
            borderWidth: 1,
          },
          {
            type: "bar",
            label: "Costs",
            data: costs,
            backgroundColor: "rgba(226,75,74,0.7)",
            borderColor: "#E24B4A",
            borderWidth: 1,
          },
          {
            type: "line",
            label: "Cumulative profit",
            data: cumProfits,
            borderColor: "#378ADD",
            backgroundColor: "rgba(55,138,221,0.08)",
            borderWidth: 2,
            pointRadius: 3,
            pointBackgroundColor: "#378ADD",
            tension: 0.3,
            fill: true,
            yAxisID: "y2",
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: {
            ticks: { font: { size: 10 }, maxRotation: 45, autoSkip: false },
            grid: { display: false },
          },
          y: {
            ticks: {
              font: { size: 10 },
              callback: (v) => "₪" + Math.round(v / 1000) + "k",
            },
            grid: { color: "rgba(128,128,128,.1)" },
          },
          y2: {
            position: "right",
            ticks: {
              font: { size: 10 },
              callback: (v) => "₪" + Math.round(v / 1000) + "k",
            },
            grid: { display: false },
          },
        },
      },
    });
    return () => {
      if (chartRef.current) chartRef.current.destroy();
    };
  }, [months, fixedCosts, contract]);

  return (
    <div style={{ position: "relative", width: "100%", height: "240px" }}>
      <canvas ref={canvasRef} role="img" aria-label="Monthly cash flow chart" />
    </div>
  );
}

function MonthWorkerCard({ worker, index, onChange, onRemove }) {
  const cost = (parseFloat(worker.daily) || 0) * (parseFloat(worker.days) || 0);
  const isExp = worker.type === "exp";
  return (
    <div className="bg-gray-50 rounded-xl border border-gray-100 overflow-hidden mb-2">
      <div
        className={`h-0.5 w-full ${isExp ? "bg-slate-600" : "bg-amber-400"}`}
      />
      <div className="p-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex gap-1 p-0.5 bg-white rounded-lg border border-gray-200">
            <button
              onClick={() => onChange(index, "type", "exp")}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${isExp ? "bg-slate-800 text-white" : "text-slate-400 hover:text-slate-600"}`}
            >
              Experienced
            </button>
            <button
              onClick={() => onChange(index, "type", "nov")}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${!isExp ? "bg-amber-400 text-white" : "text-slate-400 hover:text-slate-600"}`}
            >
              Junior
            </button>
          </div>
          <button
            onClick={() => onRemove(index)}
            className="w-6 h-6 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-400 hover:bg-red-50 transition-all text-lg leading-none"
          >
            ×
          </button>
        </div>
        {/* Stack to 1 col on mobile, 3 on sm */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Field label="Name">
            <input
              type="text"
              placeholder="e.g. Ahmad"
              value={worker.name}
              onChange={(e) => onChange(index, "name", e.target.value)}
              className={inp}
            />
          </Field>
          <Field label="Daily wage" suffix="₪">
            <input
              type="number"
              placeholder="0"
              value={worker.daily}
              onChange={(e) => onChange(index, "daily", e.target.value)}
              className={inp}
            />
          </Field>
          <Field label="Days">
            <input
              type="number"
              placeholder="0"
              value={worker.days}
              onChange={(e) => onChange(index, "days", e.target.value)}
              className={inp}
            />
          </Field>
        </div>
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200">
          <span className="text-xs text-slate-400">
            Total:{" "}
            <span className="font-semibold text-slate-700">{fmt(cost)}</span>
          </span>
          {worker.name && (
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${isExp ? "bg-slate-100 text-slate-600" : "bg-amber-50 text-amber-700"}`}
            >
              {isExp ? "Experienced" : "Junior"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function MonthEquipmentCard({ eq, index, onChange, onRemove }) {
  const cost =
    eq.type === "fixed"
      ? parseFloat(eq.cost) || 0
      : (parseFloat(eq.cost) || 0) * (parseFloat(eq.days) || 0);
  return (
    <div className="bg-gray-50 rounded-xl border border-gray-100 overflow-hidden mb-2">
      <div className="h-0.5 w-full bg-violet-400" />
      <div className="p-3">
        <div className="flex items-center gap-2 mb-3">
          <div className="flex gap-1 p-0.5 bg-white rounded-lg border border-gray-200 flex-1">
            <button
              onClick={() => onChange(index, "type", "daily")}
              className={`flex-1 py-1 rounded-md text-xs font-semibold transition-all ${eq.type === "daily" ? "bg-violet-600 text-white" : "text-slate-400 hover:text-slate-600"}`}
            >
              Daily rate × days
            </button>
            <button
              onClick={() => onChange(index, "type", "fixed")}
              className={`flex-1 py-1 rounded-md text-xs font-semibold transition-all ${eq.type === "fixed" ? "bg-violet-600 text-white" : "text-slate-400 hover:text-slate-600"}`}
            >
              Fixed rental
            </button>
          </div>
          <button
            onClick={() => onRemove(index)}
            className="w-6 h-6 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-400 hover:bg-red-50 transition-all text-lg leading-none flex-shrink-0"
          >
            ×
          </button>
        </div>
        <div
          className={`grid gap-2 grid-cols-1 ${eq.type === "daily" ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}
        >
          <Field label="Name / label">
            <input
              type="text"
              placeholder="e.g. Bobcat #2"
              value={eq.name}
              onChange={(e) => onChange(index, "name", e.target.value)}
              className={inp}
            />
          </Field>
          <Field
            label={eq.type === "fixed" ? "Total rental price" : "Daily rate"}
            suffix="₪"
          >
            <input
              type="number"
              placeholder="0"
              value={eq.cost}
              onChange={(e) => onChange(index, "cost", e.target.value)}
              className={inp}
            />
          </Field>
          {eq.type === "daily" && (
            <Field label="Days used">
              <input
                type="number"
                placeholder="0"
                value={eq.days}
                onChange={(e) => onChange(index, "days", e.target.value)}
                className={inp}
              />
            </Field>
          )}
        </div>
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200">
          <span className="text-xs text-slate-400">
            Total:{" "}
            <span className="font-semibold text-slate-700">{fmt(cost)}</span>
          </span>
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">
            {eq.type === "fixed" ? "Fixed" : `${parseFloat(eq.days) || 0} days`}
          </span>
        </div>
      </div>
    </div>
  );
}

function MonthCard({
  m,
  mi,
  fixedCosts,
  contract,
  onToggle,
  onUpdate,
  onUpdateWorker,
  onRemoveWorker,
  onAddWorker,
  onUpdateEquipment,
  onRemoveEquipment,
  onAddEquipment,
  onUpdateExpense,
  onRemoveExpense,
  onAddExpense,
}) {
  const cost = monthCost(m, fixedCosts);
  const income = parseFloat(m.billedAmount) || 0;
  const mProfit = income - cost.total;
  const pct = contract > 0 ? Math.round((income / contract) * 100) : 0;

  return (
    <div
      className={`bg-white rounded-2xl border overflow-hidden mb-3 transition-all ${m.open ? "border-slate-300" : "border-gray-100 hover:border-slate-200"}`}
    >
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer select-none"
        onClick={onToggle}
      >
        <span className="text-sm font-semibold text-slate-700">{m.label}</span>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-blue-50 text-blue-700">
            {income > 0 ? fmt(income) : "Not set"}
            {income > 0 && <span className="ml-1 opacity-60">· {pct}%</span>}
          </span>
          <span
            className={`text-xs font-medium px-2.5 py-1 rounded-full ${mProfit >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-500"}`}
          >
            {fmt(mProfit)}
          </span>
          <span
            className={`text-xs text-slate-400 transition-transform duration-200 inline-block ${m.open ? "rotate-180" : ""}`}
          >
            ▼
          </span>
        </div>
      </div>

      {m.open && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <Field label="Amount received" suffix="₪">
              <input
                type="number"
                placeholder="0"
                value={m.billedAmount}
                onChange={(e) => onUpdate("billedAmount", e.target.value)}
                className={inp}
              />
            </Field>
            <div>
              <p className="text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wide">
                % of contract
              </p>
              <div
                className={`px-3 py-2 rounded-xl border text-sm font-semibold ${contract > 0 && income > 0 ? "bg-blue-50 text-blue-700 border-blue-100" : "bg-gray-50 text-gray-300 border-gray-200"}`}
              >
                {contract > 0 && income > 0 ? pct + "%" : "—"}
              </div>
            </div>
          </div>

          {/* Workers */}
          <div className="flex items-center justify-between mt-5 mb-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              👷 Workers this month
            </p>
            <span className="text-xs text-slate-400">
              {m.workers.length} added
            </span>
          </div>
          {m.workers.map((w, wi) => (
            <MonthWorkerCard
              key={wi}
              worker={w}
              index={wi}
              onChange={(idx, field, val) => onUpdateWorker(idx, field, val)}
              onRemove={onRemoveWorker}
            />
          ))}
          <button
            onClick={onAddWorker}
            className="w-full py-2 rounded-xl border border-dashed border-gray-200 text-xs text-slate-400 hover:bg-gray-50 hover:border-slate-300 transition-all mb-1 flex items-center justify-center gap-1"
          >
            <span className="text-base leading-none">+</span> Add worker
          </button>

          {/* Equipment */}
          <div className="flex items-center justify-between mt-5 mb-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              🚜 Equipment this month
            </p>
            <span className="text-xs text-slate-400">
              {m.equipment.length} added
            </span>
          </div>
          {m.equipment.map((e, ei) => (
            <MonthEquipmentCard
              key={ei}
              eq={e}
              index={ei}
              onChange={(idx, field, val) => onUpdateEquipment(idx, field, val)}
              onRemove={onRemoveEquipment}
            />
          ))}
          <button
            onClick={onAddEquipment}
            className="w-full py-2 rounded-xl border border-dashed border-gray-200 text-xs text-slate-400 hover:bg-gray-50 hover:border-slate-300 transition-all mb-1 flex items-center justify-center gap-1"
          >
            <span className="text-base leading-none">+</span> Add equipment
          </button>

          {/* Transport */}
          <div className="flex items-center justify-between mt-5 mb-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              🚗 Transportation
            </p>
          </div>
          <div className="bg-gray-50 rounded-xl border border-gray-100 overflow-hidden mb-1">
            <div className="h-0.5 w-full bg-sky-400" />
            <div className="p-3">
              <div className="flex gap-1 p-0.5 bg-white rounded-lg border border-gray-200 mb-3">
                <button
                  onClick={() =>
                    onUpdate("transport", {
                      ...(m.transport || {}),
                      type: "fixed",
                    })
                  }
                  className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-all ${(m.transport?.type ?? "fixed") === "fixed" ? "bg-sky-500 text-white" : "text-slate-400 hover:text-slate-600"}`}
                >
                  Fixed total
                </button>
                <button
                  onClick={() =>
                    onUpdate("transport", {
                      ...(m.transport || {}),
                      type: "daily",
                    })
                  }
                  className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-all ${m.transport?.type === "daily" ? "bg-sky-500 text-white" : "text-slate-400 hover:text-slate-600"}`}
                >
                  Daily rate × days
                </button>
              </div>
              {(m.transport?.type ?? "fixed") === "fixed" ? (
                <Field label="Total transport cost this month" suffix="₪">
                  <input
                    type="number"
                    placeholder="0"
                    value={m.transport?.total ?? ""}
                    onChange={(e) =>
                      onUpdate("transport", {
                        ...(m.transport || {}),
                        type: "fixed",
                        total: e.target.value,
                      })
                    }
                    className={inp}
                  />
                </Field>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Field label="Daily rate" suffix="₪">
                    <input
                      type="number"
                      placeholder="0"
                      value={m.transport?.daily ?? ""}
                      onChange={(e) =>
                        onUpdate("transport", {
                          ...(m.transport || {}),
                          type: "daily",
                          daily: e.target.value,
                        })
                      }
                      className={inp}
                    />
                  </Field>
                  <Field label="Days">
                    <input
                      type="number"
                      placeholder="0"
                      value={m.transport?.days ?? ""}
                      onChange={(e) =>
                        onUpdate("transport", {
                          ...(m.transport || {}),
                          type: "daily",
                          days: e.target.value,
                        })
                      }
                      className={inp}
                    />
                  </Field>
                </div>
              )}
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200">
                <span className="text-xs text-slate-400">
                  Total:{" "}
                  <strong className="text-slate-700">
                    {fmt(
                      m.transport?.type === "daily"
                        ? (parseFloat(m.transport?.daily) || 0) *
                            (parseFloat(m.transport?.days) || 0)
                        : parseFloat(m.transport?.total) || 0,
                    )}
                  </strong>
                </span>
                {m.transport?.type === "daily" && m.transport?.days && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-sky-50 text-sky-700">
                    {m.transport.days} days
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Extra expenses */}
          <div className="flex items-center justify-between mt-5 mb-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              🧾 Extra expenses
            </p>
            <span className="text-xs text-slate-400">
              {(m.expenses || []).length} added
            </span>
          </div>
          <div className="bg-gray-50 rounded-xl border border-gray-100 p-3 space-y-2">
            {(m.expenses || []).map((e, ei) => (
              <div key={ei} className="flex gap-2 items-center">
                <input
                  type="text"
                  placeholder="Description (e.g. Materials)"
                  value={e.label}
                  onChange={(ev) =>
                    onUpdateExpense(ei, "label", ev.target.value)
                  }
                  className="flex-1 min-w-0 px-3 py-2 rounded-xl text-sm bg-white border border-gray-200 outline-none focus:border-slate-400 placeholder-gray-300"
                />
                <input
                  type="number"
                  placeholder="0"
                  value={e.amount}
                  onChange={(ev) =>
                    onUpdateExpense(ei, "amount", ev.target.value)
                  }
                  className="w-24 px-3 py-2 rounded-xl text-sm bg-white border border-gray-200 outline-none focus:border-slate-400 placeholder-gray-300"
                />
                <span className="text-xs text-slate-400 font-medium">₪</span>
                <button
                  onClick={() => onRemoveExpense(ei)}
                  className="w-6 h-6 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-400 hover:bg-red-50 transition-all text-lg leading-none flex-shrink-0"
                >
                  ×
                </button>
              </div>
            ))}
            <button
              onClick={onAddExpense}
              className="w-full py-2 rounded-xl border border-dashed border-gray-200 text-xs text-slate-400 hover:bg-white hover:border-slate-300 transition-all flex items-center justify-center gap-1"
            >
              <span className="text-base leading-none">+</span> Add expense
            </button>
          </div>

          {/* Month summary */}
          <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t border-gray-100 text-xs text-slate-400">
            <span>
              Workers:{" "}
              <strong className="text-slate-700">{fmt(cost.wc)}</strong>
            </span>
            <span>
              Equipment:{" "}
              <strong className="text-slate-700">{fmt(cost.ec)}</strong>
            </span>
            <span>
              Fixed: <strong className="text-slate-700">{fmt(cost.fc)}</strong>
            </span>
            <span>
              Transport:{" "}
              <strong className="text-slate-700">{fmt(cost.tr)}</strong>
            </span>
            <span>
              Other: <strong className="text-slate-700">{fmt(cost.ot)}</strong>
            </span>
            <span>
              Total cost:{" "}
              <strong className="text-slate-700">{fmt(cost.total)}</strong>
            </span>
            <span>
              Received:{" "}
              <strong className="text-emerald-600">{fmt(income)}</strong>
            </span>
            <span>
              Month profit:{" "}
              <strong
                className={mProfit >= 0 ? "text-emerald-600" : "text-red-500"}
              >
                {fmt(mProfit)}
              </strong>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function BossCalcBigProject() {
  const [projName, setProjName] = useState("");
  const [contract, setContract] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [fixedCosts, setFixedCosts] = useState([
    { label: "Base worker wages", amount: "" },
    { label: "Site management", amount: "" },
  ]);
  const [months, setMonths] = useState([]);

  useEffect(() => {
    const keys = getMonthsBetween(startDate, endDate);
    setMonths((prev) => {
      const existing = {};
      prev.forEach((m) => (existing[m.key] = m));
      return keys.map((k) => existing[k.key] || newMonthData(k.key, k.label));
    });
  }, [startDate, endDate]);

  const updateFixed = (i, f, v) =>
    setFixedCosts((p) =>
      p.map((fc, idx) => (idx === i ? { ...fc, [f]: v } : fc)),
    );
  const removeFixed = (i) =>
    setFixedCosts((p) => p.filter((_, idx) => idx !== i));
  const updateMonth = (mi, field, value) =>
    setMonths((p) =>
      p.map((m, idx) => (idx === mi ? { ...m, [field]: value } : m)),
    );
  const toggleMonth = (mi) =>
    setMonths((p) =>
      p.map((m, idx) => (idx === mi ? { ...m, open: !m.open } : m)),
    );

  const updateWorker = (mi, wi, field, value) =>
    setMonths((p) =>
      p.map((m, idx) =>
        idx !== mi
          ? m
          : {
              ...m,
              workers: m.workers.map((w, wIdx) =>
                wIdx === wi ? { ...w, [field]: value } : w,
              ),
            },
      ),
    );
  const removeWorker = (mi, wi) =>
    setMonths((p) =>
      p.map((m, idx) =>
        idx !== mi
          ? m
          : { ...m, workers: m.workers.filter((_, wIdx) => wIdx !== wi) },
      ),
    );
  const addWorker = (mi) =>
    setMonths((p) =>
      p.map((m, idx) =>
        idx !== mi
          ? m
          : {
              ...m,
              workers: [
                ...m.workers,
                { name: "", daily: "", days: "", type: "exp" },
              ],
            },
      ),
    );

  const updateEquipment = (mi, ei, field, value) =>
    setMonths((p) =>
      p.map((m, idx) =>
        idx !== mi
          ? m
          : {
              ...m,
              equipment: m.equipment.map((e, eIdx) =>
                eIdx === ei ? { ...e, [field]: value } : e,
              ),
            },
      ),
    );
  const removeEquipment = (mi, ei) =>
    setMonths((p) =>
      p.map((m, idx) =>
        idx !== mi
          ? m
          : { ...m, equipment: m.equipment.filter((_, eIdx) => eIdx !== ei) },
      ),
    );
  const addEquipment = (mi) =>
    setMonths((p) =>
      p.map((m, idx) =>
        idx !== mi
          ? m
          : {
              ...m,
              equipment: [
                ...m.equipment,
                { name: "", cost: "", type: "daily", days: "" },
              ],
            },
      ),
    );

  const updateExpense = (mi, ei, field, value) =>
    setMonths((p) =>
      p.map((m, idx) =>
        idx !== mi
          ? m
          : {
              ...m,
              expenses: (m.expenses || []).map((e, eIdx) =>
                eIdx === ei ? { ...e, [field]: value } : e,
              ),
            },
      ),
    );
  const removeExpense = (mi, ei) =>
    setMonths((p) =>
      p.map((m, idx) =>
        idx !== mi
          ? m
          : {
              ...m,
              expenses: (m.expenses || []).filter((_, eIdx) => eIdx !== ei),
            },
      ),
    );
  const addExpense = (mi) =>
    setMonths((p) =>
      p.map((m, idx) =>
        idx !== mi
          ? m
          : {
              ...m,
              expenses: [...(m.expenses || []), { label: "", amount: "" }],
            },
      ),
    );

  const contractVal = parseFloat(contract) || 0;
  const totalBilled = months.reduce(
    (s, m) => s + (parseFloat(m.billedAmount) || 0),
    0,
  );
  const totalCost = months.reduce(
    (s, m) => s + monthCost(m, fixedCosts).total,
    0,
  );
  const totalProfit = totalBilled - totalCost;
  const margin =
    totalBilled > 0 ? Math.round((totalProfit / totalBilled) * 100) : 0;
  const billedPct =
    contractVal > 0 ? Math.round((totalBilled / contractVal) * 100) : 0;
  const remaining = contractVal - totalBilled;
  const heaviestMonth =
    months.length > 0
      ? months.reduce(
          (max, m) =>
            monthCost(m, fixedCosts).total > monthCost(max, fixedCosts).total
              ? m
              : max,
          months[0],
        )
      : null;

  const exportExcel = () => {
    const rows = [];
    rows.push({ Section: "PROJECT", Item: "Name", Amount: projName, Note: "" });
    rows.push({
      Section: "PROJECT",
      Item: "Contract value",
      Amount: contractVal,
      Note: "₪",
    });
    rows.push({
      Section: "PROJECT",
      Item: "Duration",
      Amount: months.length,
      Note: "months",
    });
    rows.push({
      Section: "PROJECT",
      Item: "Total received",
      Amount: totalBilled,
      Note: `${billedPct}% of contract`,
    });
    rows.push({
      Section: "PROJECT",
      Item: "Remaining",
      Amount: remaining,
      Note: "₪ not yet billed",
    });
    rows.push({});
    rows.push({
      Section: "FIXED MONTHLY COSTS",
      Item: "",
      Amount: "",
      Note: "",
    });
    fixedCosts.forEach((f) =>
      rows.push({
        Section: "",
        Item: f.label,
        Amount: parseFloat(f.amount) || 0,
        Note: "per month",
      }),
    );
    rows.push({});
    months.forEach((m) => {
      const c = monthCost(m, fixedCosts);
      const income = parseFloat(m.billedAmount) || 0;
      const pct =
        contractVal > 0 ? Math.round((income / contractVal) * 100) : 0;
      rows.push({
        Section: m.label,
        Item: "Amount received",
        Amount: income,
        Note: `${pct}% of contract`,
      });
      m.workers.forEach((w) => {
        const wc = (parseFloat(w.daily) || 0) * (parseFloat(w.days) || 0);
        if (wc || w.name)
          rows.push({
            Section: "",
            Item: w.name || "Worker",
            Amount: wc,
            Note: `${w.type === "exp" ? "Experienced" : "Junior"} · ${w.days || 0}d × ₪${w.daily || 0}`,
          });
      });
      m.equipment.forEach((e) => {
        const ec =
          e.type === "fixed"
            ? parseFloat(e.cost) || 0
            : (parseFloat(e.cost) || 0) * (parseFloat(e.days) || 0);
        if (ec || e.name)
          rows.push({
            Section: "",
            Item: e.name || "Equipment",
            Amount: ec,
            Note:
              e.type === "fixed"
                ? "Fixed"
                : `${e.days || 0}d × ₪${e.cost || 0}`,
          });
      });
      const trNote =
        m.transport?.type === "daily"
          ? `${m.transport.days || 0}d × ₪${m.transport.daily || 0}/day`
          : "Fixed total";
      rows.push({
        Section: "",
        Item: "Equipment total",
        Amount: c.ec,
        Note: "",
      });
      rows.push({ Section: "", Item: "Fixed costs", Amount: c.fc, Note: "" });
      rows.push({ Section: "", Item: "Transport", Amount: c.tr, Note: trNote });
      (m.expenses || []).forEach((e) => {
        if (e.label || e.amount)
          rows.push({
            Section: "",
            Item: e.label || "Expense",
            Amount: parseFloat(e.amount) || 0,
            Note: "",
          });
      });
      rows.push({ Section: "", Item: "Total cost", Amount: c.total, Note: "" });
      rows.push({
        Section: "",
        Item: "Month profit",
        Amount: income - c.total,
        Note: "",
      });
      rows.push({});
    });
    rows.push({
      Section: "── TOTAL SUMMARY ──",
      Item: "",
      Amount: "",
      Note: "",
    });
    rows.push({
      Section: "Total received",
      Item: "",
      Amount: totalBilled,
      Note: `${billedPct}% billed`,
    });
    rows.push({
      Section: "Remaining",
      Item: "",
      Amount: remaining,
      Note: "not yet billed",
    });
    rows.push({ Section: "Total cost", Item: "", Amount: totalCost, Note: "" });
    rows.push({
      Section: "Net profit",
      Item: "",
      Amount: totalProfit,
      Note: `${margin}% margin`,
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 22 }, { wch: 28 }, { wch: 16 }, { wch: 30 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Big Project");
    XLSX.writeFile(
      wb,
      `calc_big_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  const exportPDF = () => {
    const profitColor = totalProfit >= 0 ? "#1D9E75" : "#E24B4A";
    let cumIncome = 0,
      cumCost = 0;
    const monthRows = months
      .map((m) => {
        const c = monthCost(m, fixedCosts);
        const income = parseFloat(m.billedAmount) || 0;
        const pct =
          contractVal > 0 ? Math.round((income / contractVal) * 100) : 0;
        const mp = income - c.total;
        cumIncome += income;
        cumCost += c.total;
        return `<tr><td><strong>${m.label}</strong></td><td>${fmt(income)}<br><span style="font-size:10px;color:#64748b">${pct}%</span></td><td style="color:#1D9E75">₪${Math.round(income).toLocaleString()}</td><td>₪${Math.round(c.wc + c.fc).toLocaleString()}</td><td>₪${Math.round(c.ec + c.tr + c.ot).toLocaleString()}</td><td style="font-weight:700;color:${mp >= 0 ? "#1D9E75" : "#E24B4A"}">₪${Math.round(mp).toLocaleString()}</td></tr>`;
      })
      .join("");
    const overallPct =
      contractVal > 0 ? Math.round((cumIncome / contractVal) * 100) : 0;
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Big Project Report</title><style>
    @media print { @page { size: A4 landscape; margin: 12mm; } body { margin: 0; } }
    * { box-sizing: border-box; } body { font-family: Arial, sans-serif; color: #1a1a1a; background: white; padding: 28px; }
    .header { background: #1e293b; color: white; padding: 20px 24px; border-radius: 12px; margin-bottom: 20px; display:flex; justify-content:space-between; align-items:center; }
    .header h1 { margin: 0 0 3px; font-size: 20px; font-weight: 700; } .header p { margin: 0; font-size: 12px; opacity: 0.6; }
    .metrics { display: grid; grid-template-columns: repeat(6,1fr); gap: 8px; margin-bottom: 20px; }
    .metric { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px; }
    .metric .label { font-size: 10px; color: #64748b; margin-bottom: 4px; text-transform: uppercase; } .metric .val { font-size: 15px; font-weight: 700; color: #1e293b; }
    .metric.profit { background: ${totalProfit >= 0 ? "#f0fdf4" : "#fff1f2"}; border-color: ${totalProfit >= 0 ? "#86efac" : "#fca5a5"}; } .metric.profit .val { color: ${profitColor}; }
    table { width:100%; border-collapse:collapse; font-size:12px; }
    th { background:#1e293b; color:white; padding:8px 10px; text-align:left; font-weight:600; }
    td { padding:8px 10px; border-bottom:1px solid #f1f5f9; vertical-align:top; }
    tr:nth-child(even) td { background:#f8fafc; }
    tfoot td { background:#1e293b; color:white; font-weight:700; padding:10px; }
    .footer { margin-top:20px; text-align:center; font-size:10px; color:#94a3b8; border-top:1px solid #f1f5f9; padding-top:12px; }
    </style></head><body>
    <div class="header"><div><h1>${projName || "Big project"} — calculation report</h1><p>Generated ${new Date().toLocaleDateString()} · ${months.length} months</p></div><div style="text-align:right"><div style="font-size:22px;font-weight:700;color:#fff">₪${Math.round(contractVal).toLocaleString()}</div><div style="font-size:11px;opacity:.6">Contract value</div></div></div>
    <div class="metrics">
      <div class="metric"><div class="label">Contract value</div><div class="val">₪${Math.round(contractVal).toLocaleString()}</div></div>
      <div class="metric"><div class="label">Total received</div><div class="val">₪${Math.round(totalBilled).toLocaleString()}</div></div>
      <div class="metric"><div class="label">Remaining</div><div class="val">₪${Math.round(remaining).toLocaleString()}</div></div>
      <div class="metric"><div class="label">Total costs</div><div class="val">₪${Math.round(totalCost).toLocaleString()}</div></div>
      <div class="metric"><div class="label">Duration</div><div class="val">${months.length} months</div></div>
      <div class="metric profit"><div class="label">Net profit</div><div class="val">₪${Math.round(totalProfit).toLocaleString()}</div></div>
    </div>
    <table><thead><tr><th>Month</th><th>Amount received</th><th>Income</th><th>Workers + fixed</th><th>Equipment + other</th><th>Month profit</th></tr></thead><tbody>${monthRows}</tbody>
    <tfoot><tr><td>Total</td><td>${overallPct}% billed</td><td style="color:#6ee7b7">₪${Math.round(cumIncome).toLocaleString()}</td><td colspan="2">₪${Math.round(cumCost).toLocaleString()}</td><td style="color:${profitColor}">₪${Math.round(totalProfit).toLocaleString()}</td></tr></tfoot></table>
    <div class="footer">NeholEsek · Big Project Calculator · ${margin}% overall margin</div>
    <script>window.onload=function(){window.print();setTimeout(function(){window.close();},500);};</script>
    </body></html>`;
    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
  };

  const metrics = [
    { label: "Contract value", val: fmt(contractVal), sub: null },
    {
      label: "Total received",
      val: fmt(totalBilled),
      sub: `${billedPct}% billed`,
    },
    {
      label: "Remaining",
      val: fmt(remaining),
      sub: `${100 - billedPct}% unbilled`,
    },
    {
      label: "Total costs",
      val: fmt(totalCost),
      sub: `${months.length} months`,
    },
    {
      label: "Heaviest month",
      val: heaviestMonth
        ? fmt(monthCost(heaviestMonth, fixedCosts).total)
        : "₪0",
      sub: heaviestMonth?.label || "—",
    },
    {
      label: "Net profit",
      val: fmt(totalProfit),
      sub: `${margin}% margin`,
      accent: true,
      positive: totalProfit >= 0,
    },
  ];

  return (
    <div className="bg-gray-50">
      {/* Metrics strip — 2 cols mobile, 3 sm, 6 md */}
      <div className="bg-slate-800 px-4 pb-4">
        <div className="max-w-3xl mx-auto grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3 pt-4">
          {metrics.map((m) => (
            <div
              key={m.label}
              className={`rounded-xl p-2.5 sm:p-3 ${m.accent ? (m.positive ? "bg-emerald-500" : "bg-red-500") : "bg-slate-700"}`}
            >
              <p
                className={`text-xs mb-1 font-medium ${m.accent ? "text-white opacity-80" : "text-slate-400"}`}
              >
                {m.label}
              </p>
              <p className="text-xs sm:text-sm font-bold text-white">{m.val}</p>
              {m.sub && (
                <p
                  className={`text-xs mt-0.5 ${m.accent ? "text-white opacity-70" : "text-slate-500"}`}
                >
                  {m.sub}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-12">
        {/* Project setup */}
        <SectionLabel icon="📋" title="Project setup" />
        <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <Field label="Project name">
              <input
                type="text"
                placeholder="e.g. Mall construction"
                value={projName}
                onChange={(e) => setProjName(e.target.value)}
                className={inp}
              />
            </Field>
            <Field label="Total contract value" suffix="₪">
              <input
                type="number"
                placeholder="0"
                value={contract}
                onChange={(e) => setContract(e.target.value)}
                className={inp}
              />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Start date">
              <input
                type="month"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={inp}
              />
            </Field>
            <Field label="End date">
              <input
                type="month"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={inp}
              />
            </Field>
          </div>
        </div>

        {/* Fixed costs */}
        <SectionLabel icon="📌" title="Fixed monthly costs" />
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <p className="text-xs text-slate-400 mb-3">
            These costs apply automatically to every month.
          </p>
          {fixedCosts.map((f, i) => (
            <div key={i} className="flex gap-2 mb-2 items-center">
              <input
                type="text"
                placeholder="Description"
                value={f.label}
                onChange={(e) => updateFixed(i, "label", e.target.value)}
                className="flex-1 min-w-0 px-3 py-2 rounded-xl text-sm bg-gray-50 border border-gray-200 outline-none focus:border-slate-400 placeholder-gray-300"
              />
              <input
                type="number"
                placeholder="0"
                value={f.amount}
                onChange={(e) => updateFixed(i, "amount", e.target.value)}
                className="w-24 px-3 py-2 rounded-xl text-sm bg-gray-50 border border-gray-200 outline-none focus:border-slate-400 placeholder-gray-300"
              />
              <span className="text-xs text-slate-400 font-medium hidden sm:inline">
                ₪/mo
              </span>
              <button
                onClick={() => removeFixed(i)}
                className="w-6 h-6 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-400 text-base leading-none flex-shrink-0"
              >
                ×
              </button>
            </div>
          ))}
          <button
            onClick={() =>
              setFixedCosts((p) => [...p, { label: "", amount: "" }])
            }
            className="w-full py-2.5 rounded-xl border border-dashed border-gray-200 text-sm text-slate-400 hover:bg-gray-50 transition-all mt-1"
          >
            + Add fixed cost
          </button>
        </div>

        {/* Month cards */}
        {months.length > 0 ? (
          <>
            <SectionLabel
              icon="📅"
              title={`Monthly plan — ${months.length} months`}
            />
            {months.map((m, mi) => (
              <MonthCard
                key={m.key}
                m={m}
                mi={mi}
                fixedCosts={fixedCosts}
                contract={contractVal}
                onToggle={() => toggleMonth(mi)}
                onUpdate={(f, v) => updateMonth(mi, f, v)}
                onUpdateWorker={(wi, f, v) => updateWorker(mi, wi, f, v)}
                onRemoveWorker={(wi) => removeWorker(mi, wi)}
                onAddWorker={() => addWorker(mi)}
                onUpdateEquipment={(ei, f, v) => updateEquipment(mi, ei, f, v)}
                onRemoveEquipment={(ei) => removeEquipment(mi, ei)}
                onAddEquipment={() => addEquipment(mi)}
                onUpdateExpense={(ei, f, v) => updateExpense(mi, ei, f, v)}
                onRemoveExpense={(ei) => removeExpense(mi, ei)}
                onAddExpense={() => addExpense(mi)}
              />
            ))}
          </>
        ) : (
          <div className="mt-6 bg-white rounded-2xl border border-gray-100 p-8 text-center">
            <p className="text-sm text-slate-400">
              Set a start and end date to generate the monthly plan.
            </p>
          </div>
        )}

        {/* Cash flow chart */}
        {months.length > 0 && (
          <>
            <SectionLabel icon="📈" title="Cash flow" />
            <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5">
              <div className="flex flex-wrap gap-4 mb-3">
                {[
                  { color: "#1D9E75", label: "Income received" },
                  { color: "#E24B4A", label: "Total costs" },
                  { color: "#378ADD", label: "Cumulative profit" },
                ].map((l) => (
                  <span
                    key={l.label}
                    className="flex items-center gap-1.5 text-xs text-slate-500"
                  >
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 2,
                        background: l.color,
                        display: "inline-block",
                      }}
                    />
                    {l.label}
                  </span>
                ))}
              </div>
              <CashFlowChart
                months={months}
                fixedCosts={fixedCosts}
                contract={contractVal}
              />
            </div>

            {/* Monthly breakdown table — scrollable on mobile */}
            <SectionLabel icon="📊" title="Monthly breakdown" />
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
                <table
                  className="w-full text-sm"
                  style={{ tableLayout: "fixed", minWidth: "520px" }}
                >
                  <thead>
                    <tr className="bg-slate-800 text-white">
                      <th
                        className="px-3 sm:px-4 py-3 text-left text-xs font-semibold"
                        style={{ width: "90px" }}
                      >
                        Month
                      </th>
                      <th
                        className="px-3 py-3 text-left text-xs font-semibold"
                        style={{ width: "110px" }}
                      >
                        Received
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold">
                        Workers
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold">
                        Equipment
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold">
                        Fixed
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold">
                        Other
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-semibold">
                        Profit
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {months.map((m, mi) => {
                      const c = monthCost(m, fixedCosts);
                      const income = parseFloat(m.billedAmount) || 0;
                      const pct =
                        contractVal > 0
                          ? Math.round((income / contractVal) * 100)
                          : 0;
                      const mp = income - c.total;
                      const isHeavy = heaviestMonth?.key === m.key;
                      return (
                        <tr
                          key={m.key}
                          className={`border-b border-gray-50 ${isHeavy ? "bg-orange-50" : mi % 2 === 0 ? "" : "bg-gray-50/50"}`}
                        >
                          <td className="px-3 sm:px-4 py-2.5 text-xs font-semibold text-slate-700">
                            {m.label}
                            {isHeavy && (
                              <span className="ml-1 text-orange-500">⚠</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-xs text-blue-600 font-medium">
                            {fmt(income)}
                            {income > 0 && (
                              <span className="text-slate-400 ml-1">
                                {pct}%
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-xs text-slate-600">
                            {fmt(c.wc)}
                          </td>
                          <td className="px-3 py-2.5 text-xs text-slate-600">
                            {fmt(c.ec)}
                          </td>
                          <td className="px-3 py-2.5 text-xs text-slate-600">
                            {fmt(c.fc)}
                          </td>
                          <td className="px-3 py-2.5 text-xs text-slate-600">
                            {fmt(c.tr + c.ot)}
                          </td>
                          <td
                            className={`px-3 py-2.5 text-xs font-semibold text-right ${mp >= 0 ? "text-emerald-600" : "text-red-500"}`}
                          >
                            {fmt(mp)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-800 text-white">
                      <td className="px-3 sm:px-4 py-3 text-xs font-semibold">
                        Total
                      </td>
                      <td className="px-3 py-3 text-xs font-semibold text-blue-300">
                        {fmt(totalBilled)}{" "}
                        <span className="opacity-60">{billedPct}%</span>
                      </td>
                      <td className="px-3 py-3 text-xs" colSpan={4}></td>
                      <td
                        className={`px-3 py-3 text-xs font-bold text-right ${totalProfit >= 0 ? "text-emerald-300" : "text-red-300"}`}
                      >
                        {fmt(totalProfit)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Export */}
        {months.length > 0 && (
          <div className="flex flex-wrap gap-3 mt-6 mb-8">
            <button
              onClick={exportExcel}
              className="flex items-center gap-2 px-5 py-3 rounded-xl bg-emerald-50 text-emerald-800 border border-emerald-200 text-sm font-semibold hover:bg-emerald-100 transition-all"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              Export Excel
            </button>
            <button
              onClick={exportPDF}
              className="flex items-center gap-2 px-5 py-3 rounded-xl bg-orange-50 text-orange-800 border border-orange-200 text-sm font-semibold hover:bg-orange-100 transition-all"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              Export PDF (landscape)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
